import React, { useState, useEffect, useRef } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { Checkbox, Select as AntSelect } from 'antd';
import { Text, SubTitle } from 'common-components/typography';
import { ICustomer, ICustomerAddress } from 'interfaces/customer-interfaces';
import SpinningLoadingActionModel from 'common-components/loading/SpinningLoadingActionModel';
import { GhostButton, PrimaryButton, SecondaryButton } from 'common-components/buttons';
import ActionModal, { ActionModalFooter } from 'common-components/modal/ActionModal';
import { AddressInput, Address, AddressInputRef } from 'views/sil-service/components/address-input';
import _ from 'lodash';

const { Option } = AntSelect;

interface CreateEditAddressModalProps {
  closeCreateEditAddressModal: () => void;
  isOpen: boolean;
  selectedCustomer: ICustomer;
  selectedAddressIndex: number;
  isCreateNewAddress: boolean;
  customerAddress: ICustomerAddress[];
  saveCustomerAddress: (addressLocation: Record<string, unknown>) => void;
  isEditBookingAddress?: boolean;
}

type FormData = {
  label: string;
  address: Address | null;
  isPrimary: boolean;
};

export const CreateEditAddressModal: React.FC<CreateEditAddressModalProps> = ({
  closeCreateEditAddressModal,
  isOpen,
  selectedAddressIndex,
  isCreateNewAddress,
  customerAddress,
  saveCustomerAddress,
}) => {
  const [isLoadingFilter, setIsLoadingFilter] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [customLabelOptions, setCustomLabelOptions] = useState<string[]>([]);
  const [isLabelDropdownOpen, setIsLabelDropdownOpen] = useState(false);
  const addressInputRef = useRef<AddressInputRef>(null);

  // Determine if this should be the primary address
  const isInitialAddressPrimary =
    selectedAddressIndex > -1 ? customerAddress[selectedAddressIndex]?.isPrimary : _.isEmpty(customerAddress);

  // Get initial address value for editing
  const initialAddress = selectedAddressIndex > -1 ? customerAddress[selectedAddressIndex] : null;

  const methods = useForm<FormData>({
    defaultValues: {
      label: initialAddress?.label || '',
      address: initialAddress,
      isPrimary: isInitialAddressPrimary,
    },
  });

  const { handleSubmit, control, watch, reset } = methods;

  // Reset form when modal opens/closes or address index changes
  useEffect(() => {
    if (isOpen) {
      const isPrimary =
        selectedAddressIndex > -1 ? customerAddress[selectedAddressIndex]?.isPrimary : _.isEmpty(customerAddress);

      const address = selectedAddressIndex > -1 ? customerAddress[selectedAddressIndex] : null;
      const label = address?.label || '';

      reset({
        label,
        address,
        isPrimary,
      });

      // Clear custom label options when modal opens
      setCustomLabelOptions([]);
      // Close the dropdown when modal opens
      setIsLabelDropdownOpen(false);
    }
  }, [isOpen, selectedAddressIndex, customerAddress, reset]);

  const handleClose = () => {
    reset({
      label: '',
      address: null,
      isPrimary: isInitialAddressPrimary,
    });
    closeCreateEditAddressModal();
  };

  const onSubmit = async (data: FormData) => {
    // Validate the address input
    const addressValidation = addressInputRef.current?.validate();

    if (addressValidation?.hasErrors) {
      return;
    }

    if (!data.address || !data.address.fullAddress) {
      return;
    }

    setIsLoadingFilter(true);

    try {
      // Transform the new Address format to the legacy format expected by parent
      // Note: We pass the coordinates directly to avoid re-geocoding
      const addressLocation = {
        streetaddress: data.address.streetAddress1 || '',
        streetaddress2: data.address.streetAddress2 || '',
        suburb: data.address.locality || '',
        state: data.address.state || '',
        postcode: data.address.postcode || '',
        country: 'Australia', // Default to Australia as Address type doesn't include country
        isPrimary: data.isPrimary,
        isManual: true, // Mark as manual to indicate all fields are provided
        // Include coordinates to avoid re-geocoding in parent
        selectedLattitude: data.address.geoLat,
        selectedLongitude: data.address.geoLng,
        label: data.label || null, // Use the separate label field from the form
      };

      saveCustomerAddress(addressLocation);
      handleClose();
    } catch (error) {
      console.error('Error saving address:', error);
    } finally {
      setIsLoadingFilter(false);
    }
  };

  const handleCancelClick = () => {
    const currentValues = watch();
    const hasChanges =
      currentValues.label !== (initialAddress?.label || '') ||
      currentValues.address?.fullAddress !== initialAddress?.fullAddress ||
      currentValues.isPrimary !== isInitialAddressPrimary;

    if (hasChanges) {
      setIsConfirmationModalOpen(true);
    } else {
      handleClose();
    }
  };

  const handleConfirmClose = () => {
    setIsConfirmationModalOpen(false);
    handleClose();
  };

  const address = watch('address');
  const isAddressValid = address?.fullAddress;

  return (
    <>
      <ActionModal
        title={<>{!isCreateNewAddress ? 'Edit' : 'Add new'} address</>}
        isOpen={isOpen}
        onClose={handleCancelClick}
      >
        <ActionModal
          isOpen={isConfirmationModalOpen}
          onClose={() => setIsConfirmationModalOpen(false)}
          title='Confirmation'
          showCloseButton={true}
        >
          <Text className='mb-medium'>You are closing the editing page without saving.</Text>
          <br />
          <Text className='mb-medium'>Do you want to proceed?</Text>
          <ActionModalFooter>
            <PrimaryButton className='mr-medium' size='large' onClick={() => setIsConfirmationModalOpen(false)}>
              Cancel
            </PrimaryButton>
            <GhostButton size='large' onClick={handleConfirmClose}>
              Proceed
            </GhostButton>
          </ActionModalFooter>
        </ActionModal>

        <div className='anim-slide-left'>
          <SpinningLoadingActionModel isOpen={isLoadingFilter} verticalAlignment='highest' />

          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className='mt-medium mb-large'>
                <SubTitle className='mb-medium'>Address Label (Optional)</SubTitle>
                <Controller
                  name='label'
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    const defaultOptions = ['Residential', 'Postal', 'Work', 'Billing'];
                    const allOptions = [...defaultOptions, ...customLabelOptions].filter(
                      (opt, index, self) => self.indexOf(opt) === index,
                    );

                    return (
                      <AntSelect
                        mode='tags'
                        value={value ? [value] : undefined}
                        open={isLabelDropdownOpen}
                        onChange={(values: string[]) => {
                          // Only keep the first value
                          const selectedValue = values && values.length > 0 ? values[0] : '';
                          onChange(selectedValue);
                          // Close the dropdown when an option is selected
                          setIsLabelDropdownOpen(false);
                        }}
                        onDropdownVisibleChange={setIsLabelDropdownOpen}
                        placeholder='Select or type a label and press Enter'
                        size='large'
                        style={{ width: '100%' }}
                        allowClear
                        maxTagCount={1}
                        dropdownStyle={{ zIndex: 10000 }}
                      >
                        {allOptions.map((opt) => (
                          <Option key={opt} value={opt}>
                            {opt}
                          </Option>
                        ))}
                      </AntSelect>
                    );
                  }}
                />
              </div>

              <div className='mb-large'>
                <SubTitle className='mb-medium'>Address</SubTitle>
                <Controller
                  name='address'
                  control={control}
                  rules={{ required: 'Address is required' }}
                  render={({ field }) => (
                    <AddressInput
                      {...field}
                      ref={addressInputRef}
                      width='100%'
                      autocompleteGeoComponentRestrictions={{ country: 'AU' }}
                      size='lg'
                      hideManualInputButton={false}
                    />
                  )}
                />
              </div>

              <div className='mb-medium'>
                <Controller
                  name='isPrimary'
                  control={control}
                  render={({ field }) => (
                    <Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)}>
                      This is the customer's primary address
                    </Checkbox>
                  )}
                />
              </div>

              <ActionModalFooter>
                <SecondaryButton size='large' onClick={handleCancelClick} className='mr-medium'>
                  Cancel
                </SecondaryButton>
                <PrimaryButton size='large' htmlType='submit' disabled={!isAddressValid}>
                  {!isCreateNewAddress ? 'Save' : 'Add Address'}
                </PrimaryButton>
              </ActionModalFooter>
            </form>
          </FormProvider>
        </div>
      </ActionModal>
    </>
  );
};

export default CreateEditAddressModal;
